import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useDebounce } from 'use-debounce';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import DataSheet from '@/components/Modal/Project/DataSheet';

import project from '@/api/project';

const { getProducts } = project();

export default function DataSheets() {
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize, order_by: '-productid' };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: ['projects-file', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProducts(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			const count = d?.data?.count || d?.data?.length
			if (isFetched && count ) {
				oC.current = count;
				return count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	const dataList = useMemo(() => d?.data?.results ||  d?.data || [], [d]) || [];

	const columns = [
		{
			field: 'productid',
			headerName: 'Nr',
			filterable: false,
			sortable: true,
			width: 56,
			renderCell: (index) => {
				const pM = paginationModel;
				const idx = index.api.getAllRowIds().indexOf(index.id) + 1;
				return idx + pM.page * pM.pageSize;
			},
		},
		{
			field: 'productprefix',
			headerName: 'Kode',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productcodenumber',
			headerName: 'Nummer',
			headerAlign: 'center',
			align: 'center',
			width: 144,
		},
		{
			field: 'productname',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'productversion',
			headerName: 'Versjon',
			headerAlign: 'center',
			align: 'center',
		},
	];

	const onSaveDataSheet = () => {};

	return (
		<>
			<BDHelmet title='Datasheet' description='BDS Samferdsel project datasheet' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk i dokument her ...'
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
					</div>
					<div>
						<span className='font-bold'>Vedlegg </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>total</span>
					</div>
					<div>
						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							// rowsPerPageOptions={[10, 20, 50]}
							className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							getRowId={(row) => row.productid}
							slots={{
								pagination: Pagination,
							}}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRow?.productid === params.row.productid) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectedRow(params.row);
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
				</div>
				{!!selectedRow && (
					<DataSheet open={!!selectedRow} onSave={onSaveDataSheet} data={selectedRow} onClose={() => setSelectedRow(false)} />
				)}
			</div>
		</>
	);
}
